<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" @click.self="$emit('close')">
    <div class="w-full max-w-md bg-surface dark:bg-surface-dark rounded-xl shadow-xl max-h-[90vh] flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 border-b border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <h2 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">{{ title }}</h2>
        <button @click="$emit('close')" class="text-fg-faint hover:text-fg-secondary dark:hover:text-fg-secondary-dark">
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="px-6 py-4 space-y-3 overflow-y-auto">
        <p v-if="subtitle" class="text-sm text-fg-muted dark:text-fg-muted-dark">{{ subtitle }}</p>
        <label v-if="showDatePickerEffective" class="block text-xs text-fg-muted dark:text-fg-muted-dark">
          Reappear on
          <input
            v-model="followUpDate"
            type="date"
            :min="_todayLocal()"
            class="block mt-0.5 w-full px-3 py-2 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </label>
        <label v-if="showTimestamp" class="block text-xs text-fg-muted dark:text-fg-muted-dark">
          When
          <input
            v-model="occurredAt"
            type="datetime-local"
            class="block mt-0.5 w-full px-3 py-2 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </label>
        <textarea
          v-model="note"
          rows="3"
          :placeholder="placeholder"
          autofocus
          class="w-full px-3 py-2 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
        />
        <div v-if="reopenModes" class="flex flex-col gap-2">
          <button
            v-for="opt in reopenModes"
            :key="opt[0]"
            type="button"
            @click="reopenMode = opt[0]"
            :class="[
              'px-3 py-2 text-sm text-left rounded-lg border transition-colors',
              reopenMode === opt[0]
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                : 'border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark'
            ]"
          >{{ opt[1] }}</button>
        </div>
        <div v-if="showCheckinOutcome">
          <p class="text-xs text-fg-muted dark:text-fg-muted-dark mb-1">Resolve the check-in as:</p>
          <div class="flex gap-2">
            <button
              v-for="opt in [['converted', 'Converted'], ['follow_up', 'Follow Up'], ['lost', 'Lost']]"
              :key="opt[0]"
              type="button"
              @click="checkinOutcome = opt[0]"
              :class="[
                'flex-1 px-2 py-1.5 text-sm rounded-lg border transition-colors',
                checkinOutcome === opt[0]
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark'
              ]"
            >{{ opt[1] }}</button>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-3 px-6 py-4 border-t border-stroke-subtle dark:border-stroke-dark flex-shrink-0">
        <button
          type="button"
          @click="$emit('close')"
          :disabled="saving"
          class="px-4 py-2 text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark rounded-lg transition-colors disabled:opacity-50"
        >{{ noteRequired ? 'Skip' : 'Cancel' }}</button>
        <button
          type="button"
          @click="save"
          :disabled="saving || (noteRequired && !note.trim()) || (showCheckinOutcome && !checkinOutcome) || (reopenModes && !reopenMode) || (showDatePickerEffective && !followUpDate)"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
        >{{ saving ? 'Saving…' : saveLabel }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  placeholder: { type: String, default: 'Add a note…' },
  saving: { type: Boolean, default: false },
  // When false, the note is optional and Save is always enabled — used by the
  // quick-log call/text/email actions, where the log itself matters more than
  // the note (most of the action is self-explanatory; a note is a bonus, not
  // a requirement). When true (the default, matching every pre-existing
  // caller), Save stays disabled until something is typed.
  noteRequired: { type: Boolean, default: true },
  // Shows an editable "when did this happen" field, defaulting to now — for
  // logging something after the fact with its real time rather than the
  // moment it was typed up.
  showTimestamp: { type: Boolean, default: false },
  saveLabel: { type: String, default: 'Save Note' },
  // Shows a Converted/Follow Up/Lost picker and requires a choice before
  // Save is enabled — used when logging this call/text also resolves the
  // signup's check-in stage (plan 44 follow-up, 2026-09-03), so the same
  // click covers both instead of needing separate stage buttons.
  showCheckinOutcome: { type: Boolean, default: false },
  // Shows a future date picker ("Reappear on") — used for scheduling a
  // Follow Up snooze, where the note is optional but a date is required.
  // Also shown whenever the Follow Up checkin outcome is picked below —
  // resolving a check-in as Follow Up snoozes the card the same way.
  showDatePicker: { type: Boolean, default: false },
  // [[key, label], ...] — Reopen a lost signup: a required choice of where it picks
  // up; 'follow_up' also needs the date picker.
  reopenModes: { type: Array, default: null },
})
const emit = defineEmits(['save', 'close'])

const note = ref('')
const checkinOutcome = ref(null)
const reopenMode = ref(null)
const showDatePickerEffective = computed(() => props.showDatePicker || checkinOutcome.value === 'follow_up' || reopenMode.value === 'follow_up')

function _nowLocal() {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}
function _todayLocal() {
  return _nowLocal().slice(0, 10)
}
const occurredAt = ref(_nowLocal())
const followUpDate = ref('')

function save() {
  const payload = { note: note.value.trim() }
  if (props.showTimestamp) {
    // datetime-local has no timezone — treat it as this browser's local time
    // and convert to a real UTC instant, same as new Date(localString) does.
    payload.occurredAt = occurredAt.value ? new Date(occurredAt.value).toISOString() : null
  }
  if (props.showCheckinOutcome) {
    payload.checkinOutcome = checkinOutcome.value
  }
  if (props.reopenModes) {
    payload.reopenMode = reopenMode.value
  }
  if (showDatePickerEffective.value) {
    payload.followUpDate = followUpDate.value
  }
  emit('save', payload)
}
</script>
